import { useContext, useEffect, useState } from 'react';
import AuthContext from '../components/shared/AuthContext';
import { useLearningRealtime } from '../realtime/LearningRealtimeProvider';
import { REALTIME_UPDATED_EVENT } from '../realtime/realtimeBus';

const DEFAULT_INTERVAL_MS = 20_000;

/**
 * Live learning + usage counters. Uses app-wide LearningRealtimeProvider when mounted.
 */
export default function useLiveLearningStats(options = {}) {
  const { enabled = true, intervalMs = DEFAULT_INTERVAL_MS } = options;
  const realtime = useLearningRealtime();
  const { isAuthenticated, isLoading: authLoading } = useContext(AuthContext);

  const [fallbackStats, setFallbackStats] = useState(null);
  const [fallbackUsage, setFallbackUsage] = useState(null);
  const [fallbackUpdatedAt, setFallbackUpdatedAt] = useState(null);
  const [fallbackRefreshing, setFallbackRefreshing] = useState(false);
  const [fallbackError, setFallbackError] = useState('');

  const hasProvider = realtime != null;

  useEffect(() => {
    if (hasProvider || !enabled) return undefined;
    const onUpdate = (e) => {
      const snap = e.detail;
      setFallbackStats(snap?.learning || null);
      setFallbackUsage(snap?.usage || null);
      setFallbackUpdatedAt(new Date());
    };
    window.addEventListener(REALTIME_UPDATED_EVENT, onUpdate);
    return () => window.removeEventListener(REALTIME_UPDATED_EVENT, onUpdate);
  }, [hasProvider, enabled]);

  if (hasProvider) {
    return {
      stats: realtime.learning,
      usage: realtime.usage,
      updatedAt: realtime.updatedAt,
      refreshing: realtime.refreshing,
      error: realtime.error,
      refresh: realtime.refresh,
      dueCount: realtime.dueCount,
      savedCount: realtime.savedCount,
      reviewedToday: realtime.reviewedToday,
    };
  }

  return {
    stats: fallbackStats,
    usage: fallbackUsage,
    updatedAt: fallbackUpdatedAt,
    refreshing: fallbackRefreshing,
    error: fallbackError,
    refresh: () => {},
    dueCount: Number(fallbackStats?.dueCount ?? 0),
    savedCount: Number(fallbackStats?.savedCount ?? 0),
    reviewedToday: Number(fallbackStats?.reviewedToday ?? 0),
  };
}
